import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FolderArrowDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { apiCall } from '../services/api';

interface FolderNode {
  path: string;
  name: string;
  depth?: number;
}

interface BulkMoveModalProps {
  bucketName: string;
  selectedFiles: Set<string>;
  onSuccess: (movedCount: number, failed: Array<{ key: string; reason: string }>) => void;
  onError: (message: string) => void;
  onCancel: () => void;
}

const BulkMoveModal: React.FC<BulkMoveModalProps> = ({
  bucketName,
  selectedFiles,
  onSuccess,
  onError,
  onCancel
}) => {
  const [destination, setDestination] = useState('');
  const [folders, setFolders] = useState<FolderNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(true);

  // Check if any selected key is a folder
  const hasFolderSelection = Array.from(selectedFiles).some((k) => k.endsWith('/'));

  // Validation: destination must be empty or end with /
  const isDestinationValid = destination === '' || destination.endsWith('/');

  useEffect(() => {
    const fetchFolders = async () => {
      setFoldersLoading(true);
      try {
        const response = await apiCall(`/s3/folder-structure/${bucketName}`);
        setFolders(Array.isArray(response?.folders) ? response.folders : []);
      } catch {
        setFolders([]);
      } finally {
        setFoldersLoading(false);
      }
    };
    fetchFolders();
  }, [bucketName]);

  const handleConfirm = async () => {
    if (!isDestinationValid) return;
    setLoading(true);
    try {
      const response = await apiCall(`/s3/bulk-move/${bucketName}`, {
        method: 'POST',
        body: JSON.stringify({ keys: Array.from(selectedFiles), destinationPrefix: destination })
      });

      const moved: string[] = response.moved || [];
      const failed: Array<{ key: string; reason: string }> = response.failed || [];

      onSuccess(moved.length, failed);
    } catch (error: any) {
      onError(error.message || 'Bulk move failed');
      // Keep modal open
    } finally {
      setLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
        >
          <div className="text-center mb-5">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-3">
              <FolderArrowDownIcon className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Move {selectedFiles.size} {selectedFiles.size === 1 ? 'Item' : 'Items'}</h3>
            <p className="text-sm text-gray-500 mt-1">Select a destination folder or type a prefix.</p>
          </div>

          {/* Folder items warning */}
          {hasFolderSelection && (
            <div className="flex items-start space-x-2 bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-700">Folder items will be skipped — only files can be moved.</p>
            </div>
          )}

          {/* Folder tree picker */}
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Select destination folder:</p>
            {foldersLoading ? (
              <div className="flex items-center text-sm text-gray-500 py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Loading folders...
              </div>
            ) : folders.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No folders found. Type a destination below.</p>
            ) : (
              <div className="border border-gray-200 rounded-md max-h-40 overflow-y-auto">
                <button
                  onClick={() => setDestination('')}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 ${destination === '' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                >
                  / (bucket root)
                </button>
                {folders.map((folder) => (
                  <button
                    key={folder.path}
                    onClick={() => setDestination(folder.path)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${destination === folder.path ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                    style={{ paddingLeft: `${12 + (folder.depth || 0) * 16}px` }}
                  >
                    📁 {folder.path}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Text input fallback */}
          <div className="mb-4">
            <label htmlFor="bulk-move-destination" className="block text-sm font-medium text-gray-700 mb-1">
              Destination prefix (must be empty or end with /):
            </label>
            <input
              id="bulk-move-destination"
              type="text"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="folder/ or leave empty for root"
            />
            {destination !== '' && !destination.endsWith('/') && (
              <p className="text-xs text-red-600 mt-1">Destination must end with / or be empty</p>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!isDestinationValid || loading}
              className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Moving...
                </span>
              ) : (
                `Move ${selectedFiles.size} ${selectedFiles.size === 1 ? 'Item' : 'Items'}`
              )}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  , document.body);
};

export default BulkMoveModal;
